import { intlTimeZone, supportedTimeZones } from '@/shared/lib/timeZones';

import type { CompanyProfile, IsoDate } from '../api';

/**
 * The letter of the alphabetical index (mirrors the backend's `User.IndexLetter`): the upper-case first letter of the
 * last name, `Ё` filed under `Е`; `#` for names that do not start with a letter.
 */
export function indexLetter(lastName: string): string {
  const first = lastName.trimStart().charAt(0);
  if (!first || !/\p{L}/u.test(first)) return '#';
  const upper = first.toLocaleUpperCase();
  return upper === 'Ё' ? 'Е' : upper;
}

export interface LetterSection<T> {
  letter: string;
  items: T[];
}

/**
 * Splits a list that is already sorted by last name into sections by index letter, keeping the order; a letter that
 * appears again later (another page) continues its section.
 */
export function sectionsByLetter<T extends { lastName: string }>(items: readonly T[]): LetterSection<T>[] {
  const sections: LetterSection<T>[] = [];
  const byLetter = new Map<string, LetterSection<T>>();
  for (const item of items) {
    const letter = indexLetter(item.lastName);
    let section = byLetter.get(letter);
    if (!section) {
      section = { letter, items: [] };
      byLetter.set(letter, section);
      sections.push(section);
    }
    section.items.push(item);
  }
  return sections;
}

/** Profile fields an employee fills in themselves (the completeness meter on the own profile). */
export const PROFILE_FIELDS = ['avatar', 'phones', 'birthday', 'about', 'skills'] as const;
export type ProfileField = (typeof PROFILE_FIELDS)[number];

export interface Completeness {
  /** 0–100, rounded. */
  percent: number;
  missing: ProfileField[];
}

/** How complete a profile is (name, e-mail, position and department always count as filled). */
export function profileCompleteness(
  profile: Pick<CompanyProfile, 'avatarUrl' | 'phones' | 'birthday' | 'about' | 'skills'>,
): Completeness {
  const filled: Record<ProfileField, boolean> = {
    avatar: !!profile.avatarUrl,
    phones: profile.phones.length > 0,
    birthday: !!profile.birthday,
    about: !!profile.about?.trim(),
    skills: profile.skills.length > 0,
  };
  const missing = PROFILE_FIELDS.filter((field) => !filled[field]);
  const base = 2; // name and e-mail
  const total = base + PROFILE_FIELDS.length;
  return { percent: Math.round(((total - missing.length) / total) * 100), missing };
}

/** `yyyy-MM-dd` of a date in the local calendar. */
export function toIsoDate(date: Date): IsoDate {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parses `yyyy-MM-dd` as a local calendar date (not UTC midnight). */
export function parseIsoDate(value: IsoDate): Date {
  const [y = 1970, m = 1, d = 1] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Days from `today` to the next birthday (0 = today); 29 February counts as 28 February in other years. */
export function daysUntilBirthday(birthday: IsoDate, today: Date): number {
  const born = parseIsoDate(birthday);
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const next = (year: number) => {
    const month = born.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    return new Date(year, month, Math.min(born.getDate(), lastDay));
  };
  let candidate = next(start.getFullYear());
  if (candidate < start) candidate = next(start.getFullYear() + 1);
  return Math.round((candidate.getTime() - start.getTime()) / 86_400_000);
}

/** Whole years and remaining months since `hireDate` (negative spans clamp to zero). */
export function tenure(hireDate: IsoDate, today: Date): { years: number; months: number } {
  const start = parseIsoDate(hireDate);
  let months = (today.getFullYear() - start.getFullYear()) * 12 + (today.getMonth() - start.getMonth());
  if (today.getDate() < start.getDate()) months -= 1;
  months = Math.max(0, months);
  return { years: Math.floor(months / 12), months: months % 12 };
}

/**
 * The employee's local time and how far it is from the viewer's, in whole hours (e.g. +2), or null when the zone is
 * unknown to the browser.
 */
export function localTime(
  timeZone: string,
  now: Date,
  intlLocale: string,
): { time: string; offsetHours: number } | null {
  try {
    const time = new Intl.DateTimeFormat(intlLocale, {
      timeZone: intlTimeZone(timeZone),
      hour: '2-digit',
      minute: '2-digit',
    }).format(now);
    const offsetHours = Math.round((zoneOffsetMinutes(timeZone, now) + now.getTimezoneOffset()) / 60);
    return { time, offsetHours };
  } catch {
    return null;
  }
}

/** Minutes the zone is ahead of UTC at `instant`. */
export function zoneOffsetMinutes(timeZone: string, instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: intlTimeZone(timeZone),
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'));
  return Math.round((asUtc - Math.floor(instant.getTime() / 60_000) * 60_000) / 60_000);
}

/**
 * IANA time zones the browser knows under their current names (for the profile's time zone select: Kyiv is offered as
 * "Europe/Kyiv", not as the "Europe/Kiev" browsers list), with a fallback list on older engines.
 */
export function timeZoneOptions(): string[] {
  return supportedTimeZones();
}

/** The full profile page of an employee. */
export const profilePath = (userId: string) => `/company/users/${encodeURIComponent(userId)}`;

/** "UTC+03:00" for an offset in minutes. */
export function formatOffset(minutes: number): string {
  const sign = minutes >= 0 ? '+' : '−';
  const abs = Math.abs(minutes);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

/** Image types the server accepts as avatars (it checks the content too) and the size limit. */
export const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const MAX_AVATAR_BYTES = 10 * 1024 * 1024;

/** Client-side check of an avatar before uploading; returns the i18n key of the problem or null. */
export function avatarProblem(file: Pick<File, 'type' | 'size'>): string | null {
  if (!AVATAR_TYPES.includes(file.type)) return 'errors.avatar_invalid';
  if (file.size > MAX_AVATAR_BYTES) return 'errors.avatar_too_large';
  return null;
}

/** The password policy of the identity module: at least 8 characters with at least 4 different ones. */
export const isStrongEnough = (password: string): boolean =>
  password.length >= 8 && new Set(password).size >= 4;

/** Links of the contact actions (MODULE-CONTRACTS §2): messenger chat, video call, a new task for the employee. */
export const contactLinks = {
  message: (userId: string) => `/messenger?user=${encodeURIComponent(userId)}`,
  call: (userId: string) => `/messenger?user=${encodeURIComponent(userId)}&call=video`,
};

/** The current URL's search with a `task:new` panel on top, prefilled with the responsible employee. */
export function newTaskSearch(currentSearch: string, responsibleId: string): string {
  const params = new URLSearchParams(currentSearch);
  const panels = params.getAll('panel').filter((panel) => panel !== 'task:new');
  params.delete('panel');
  for (const panel of panels) params.append('panel', panel);
  params.append('panel', 'task:new');
  params.set('taskResponsible', responsibleId);
  return `?${params.toString()}`;
}

/** `tel:` link of a free-form phone number ("+7 (495) 120-45-00, доб. 101" → `tel:+74951204500,101`). */
export function telLink(phone: string): string {
  const [main = '', extension] = phone.split(/,?\s*(?:доб\.|ext\.?|дод\.)\s*/iu);
  const digits = main.replace(/[^\d+]/gu, '');
  const ext = extension?.replace(/\D/gu, '');
  return `tel:${digits}${ext ? `,${ext}` : ''}`;
}
